package dev.lingvo.site.ds

import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.log10
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.roundToInt

/** A zero-inclusive vertical scale shared by the drawing and its deterministic geometry tests. */
internal data class LineChartAxis(
    val minimum: Double,
    val maximum: Double,
    val ticks: List<Double>,
) {
    fun y(value: Double, plotTop: Double, plotHeight: Double): Double =
        plotTop + plotHeight * (1.0 - (value - minimum) / (maximum - minimum))
}

internal fun lineChartAxis(values: List<Double>): LineChartAxis {
    val finiteValues = values.filter { it.isFinite() }
    val lowestValue = min(0.0, finiteValues.minOrNull() ?: 0.0)
    val highestValue = max(0.0, finiteValues.maxOrNull() ?: 0.0)
    if (lowestValue == 0.0 && highestValue == 0.0) {
        return LineChartAxis(
            minimum = 0.0,
            maximum = 1.0,
            ticks = listOf(0.0, 1.0),
        )
    }

    val rawStep = (highestValue - lowestValue) / LINE_CHART_TARGET_TICK_INTERVALS
    // The default formatter is a whole count. A half-unit grid printed "0, 1, 1, 2" and the old
    // de-duplication then discarded the actual scale maximum, putting a value of two above its own
    // plot. Count ticks stay at least one apart; custom formatters still retain exact point values.
    val step = max(1.0, niceTickStep(rawStep))
    val minimum = floor(lowestValue / step) * step
    val maximum = ceil(highestValue / step) * step
    val intervalCount = ((maximum - minimum) / step).roundToInt()
    val ticks = (0..intervalCount).map { index ->
        val value = minimum + step * index
        if (value == -0.0) 0.0 else value
    }
    return LineChartAxis(minimum = minimum, maximum = maximum, ticks = ticks)
}

/** Round intervals to the familiar 1 / 2 / 5 sequence at any order of magnitude. */
private fun niceTickStep(rawStep: Double): Double {
    if (!rawStep.isFinite() || rawStep <= 0.0) return 1.0
    val magnitude = 10.0.pow(floor(log10(rawStep)))
    val fraction = rawStep / magnitude
    val niceFraction = when {
        fraction < 1.5 -> 1.0
        fraction < 3.0 -> 2.0
        fraction < 7.0 -> 5.0
        else -> 10.0
    }
    return niceFraction * magnitude
}

private const val LINE_CHART_TARGET_TICK_INTERVALS = 4.0
